/**
 * The link in the weekly email: `<site>/projects?whatsnew=1` opens the What's new drawer.
 *
 * ⚠️ IT HAS TO SURVIVE SIGN-IN. Somebody who clicks the link in an email is very likely to be signed out, and signing in is a
 * redirect to Microsoft and back; whether the way back restores the query string is the identity library's business and not
 * something this app controls. So the sign-in screen, the one place shown only to somebody who is not signed in yet, remembers
 * the link for the tab, and the header takes it once `me` has loaded. This is the same arrangement as the feedback emails'
 * `?feedback=mine` (`lib/feedbackMine.ts`), kept apart so the two cannot trip over each other.
 */

export const WHATS_NEW_LINK_PARAM = 'whatsnew';

/** Where the link is remembered across the sign-in screen: this tab only, and gone when it closes. */
const STASH_KEY = 'pt-open-whats-new';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The tab's storage, or `undefined` where it cannot be had (a private window, blocked site data). It can throw on access. */
function tabStorage(): Store | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Storage can fail on any call, and nothing here is worth failing over: the worst case is a drawer that does not open itself. */
function attempt<T>(fn: () => T, otherwise: T): T {
  try {
    return fn();
  } catch {
    return otherwise;
  }
}

/** Does this query string carry the link? Any value counts: `?whatsnew=1` is what the email says. */
export function hasWhatsNewLink(search: string): boolean {
  return new URLSearchParams(search).get(WHATS_NEW_LINK_PARAM) !== null;
}

/** Remember, before sign-in, that the page was opened from the email. Called from the sign-in screen. */
export function rememberWhatsNewLink(search: string, store: Store | undefined = tabStorage()): void {
  if (hasWhatsNewLink(search)) attempt(() => store?.setItem(STASH_KEY, '1'), undefined);
}

/**
 * Should the drawer open now? Yes if the link is in the URL, or was remembered before sign-in. Taking it forgets the memory, so a
 * later reload does not open the drawer again.
 */
export function takeWhatsNewLink(search: string, store: Store | undefined = tabStorage()): boolean {
  const remembered = attempt(() => store?.getItem(STASH_KEY) === '1', false);
  if (remembered) attempt(() => store?.removeItem(STASH_KEY), undefined);
  return remembered || hasWhatsNewLink(search);
}

/** The query string without the link, so a reload does not open the drawer again. Other parameters are left alone. */
export function withoutWhatsNewLink(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(WHATS_NEW_LINK_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
